import { Main } from "../Main";
import { IEnvironmentObjectConfig, EnvironmentObject } from "./EnvironmentObject";
import { CubeTextureLoader, TextureLoader, Texture, RepeatWrapping, CircleGeometry, MeshStandardMaterial, Mesh, Scene, LinearSRGBColorSpace, SRGBColorSpace } from "three";

export interface IEnvironmentConfig
{
	id: string;
	label: string;
	skybox: string;
	ground: {
		texture: string;
		normal: string;
		repeatX?: number;
		repeatY?: number;
	}
	objects?: IEnvironmentObjectConfig[];
}

export class Environment
{
	private _config: IEnvironmentConfig;
	private _scene: Scene;

	// Reuse meshes as all environments use these
	public static sky: Mesh;
	public static ground: Mesh;

	private _textureCube: Texture;
	private _groundTexture: Texture;

	private _objects: EnvironmentObject[];

	private _textureLoader: TextureLoader;

	constructor(config: IEnvironmentConfig, scene: Scene, textureLoader: TextureLoader)
	{
		this._textureLoader = textureLoader;

		this._config = config;
		this._scene = scene;
	}

	public async show()
	{
		await this.updateSky();
		await this.updateGround();
		this.addObjects();

		Main.getInstance().render = true;
	}

	public hide()
	{
		this.removeObjects();

		Main.getInstance().render = true;
	}

	// ------------------------------------------------------------------------------------------------------
	// Sky

	private updateSky()
	{
		return new Promise<boolean>((resolve, reject) =>
		{
			if (!this._textureCube)
			{
				var path = `assets/skybox/${this._config.skybox}/`;
				var format = '.jpg';
				var urls = [
					path + 'px' + format, path + 'nx' + format,
					path + 'py' + format, path + 'ny' + format,
					path + 'pz' + format, path + 'nz' + format
				];

				this._textureCube = new CubeTextureLoader().load(urls, undefined, function() {
					Main.getInstance().render = true;
					resolve(true);
				});
			}

			this._scene.background = this._textureCube;
			//this._scene.fog = new Fog(0xcce0ff, 10, 800);
			resolve(true);
		});
	}

	// ------------------------------------------------------------------------------------------------------
	// Ground

	private updateGround()
	{
		return new Promise<boolean>((resolve, reject) =>
		{
			this.createGround();

			if (!this._groundTexture)
			{
				this.loadTexture(`assets/ground/${this._config.ground.texture}`, (texture: Texture)=>
				{
					texture.colorSpace = SRGBColorSpace;
					this._groundTexture = texture;
					(<MeshStandardMaterial>Environment.ground.material).map = this._groundTexture;
					(<MeshStandardMaterial>Environment.ground.material).needsUpdate = true;
					Main.getInstance().render = true;

					if (this._config.ground.normal)
					{
						this.loadTexture(`assets/ground/${this._config.ground.normal}`, (texture: Texture)=>
						{
							(<MeshStandardMaterial>Environment.ground.material).normalMap = texture;
							//(<MeshStandardMaterial>Environment.ground.material).normalScale.set(2, 2);
							(<MeshStandardMaterial>Environment.ground.material).needsUpdate = true;
							Main.getInstance().render = true;
							resolve(true);
						});
					}
					else
					{
						(<MeshStandardMaterial>Environment.ground.material).normalMap = null;
						(<MeshStandardMaterial>Environment.ground.material).needsUpdate = true;
						Main.getInstance().render = true;
						resolve(true);
					}
				});
			}
			else
			{
				resolve(true);
			}
		});
	}

	private loadTexture(url: string, onLoad: (texture: Texture) => void)
	{
		const loader = this._textureLoader;

		loader.load(url, (texture: Texture) =>
		{
			texture.wrapS = texture.wrapT = RepeatWrapping;

			let repeatX = 90;
			let repeatY = 90;

			if (this._config.ground.repeatX)
			{
				repeatX = this._config.ground.repeatX;
			}
			if (this._config.ground.repeatY)
			{
				repeatY = this._config.ground.repeatY;
			}

			texture.repeat.x = repeatX;
			texture.repeat.y = repeatY;

			onLoad(texture);
		});
	};

	private createGround()
	{
		this._groundTexture = null;
		if (Environment.ground)
		{
			return;
		}

		const geom = new CircleGeometry(4000, 30);

		const mat = new MeshStandardMaterial({
			//map: groundTexture,
			roughness: 1,
			metalness: 0,
		});

		Environment.ground = new Mesh(geom, mat);
		//ground.scale.x = ground.scale.y = 1000;
		Environment.ground.rotation.x = -Math.PI/2;
		Environment.ground.receiveShadow = true;

		this._scene.add(Environment.ground);
	}

	// ------------------------------------------------------------------------------------------------------
	// Objects

	private addObjects()
	{
		this.createObjects();

		for (var i = 0; i < this._objects.length; ++i)
		{
			this._scene.add(this._objects[i].container);
		}
	}

	private removeObjects()
	{
		if (!this._objects)
		{
			return;
		}

		for (var i = 0; i < this._objects.length; ++i)
		{
			this._scene.remove(this._objects[i].container);
		}
	}

	private createObjects()
	{
		if (this._objects)
		{
			return;
		}

		this._objects = [];

		var objects = this._config.objects;

		if (objects)
		{
			for (var i = 0; i < objects.length; ++i)
			{
				this._objects.push(new EnvironmentObject(objects[i]));
			}
		}
	}

	public get label()
	{
		return this._config.label;
	}

	public get id()
	{
		return this._config.id;
	}

	public get textureCube(): Texture
	{
		return this._textureCube;
	}
}